import { formatEuroFr } from './editProductOutcome.js'

export const MAX_VISIBLE_TOASTS = 3
export const TOAST_DEDUPE_WINDOW = 4000
export const TOAST_LEAVE_DURATION = 260
export const STALE_NOTIFICATION_AGE = 15 * 60 * 1000

const KIND_BY_TYPE = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  urgent: 'warning',
  info: 'info',
}

const DURATION_BY_KIND = {
  success: 5000,
  info: 4000,
  warning: 6000,
  error: 6000,
}

/** Les quatre familles de pictogrammes : validation, information, avertissement, erreur. */
export function notificationKind(type) {
  return KIND_BY_TYPE[type] || 'info'
}

/** Durée d’expiration du toast, ou null quand la notification ne disparaît pas seule. */
export function toastDuration(notification) {
  if (!notification) return null
  if (notification.showToast === false) return null
  if (notification.autoDismiss === false) return null
  if (Array.isArray(notification.actions) && notification.actions.length > 0) return null
  const explicit = Number(notification.duration)
  if (Number.isFinite(explicit) && explicit > 0) return explicit
  if (notification.type === 'urgent') return 8000
  return DURATION_BY_KIND[notificationKind(notification.type)]
}

/** Les annonces suivent l’importance : assertive pour les alertes, polie sinon. */
export function announcement(kind) {
  return kind === 'error' || kind === 'warning'
    ? { role: 'alert', ariaLive: 'assertive' }
    : { role: 'status', ariaLive: 'polite' }
}

export function notificationCopy(notification) {
  if (typeof notification === 'string') {
    return { title: '', message: notification.trim() }
  }
  return {
    title: String(notification?.title || '').trim(),
    message: String(notification?.message || '').trim(),
  }
}

export function hasNotificationCopy(notification) {
  const { title, message } = notificationCopy(notification)
  return Boolean(title || message)
}

export function normalizeNotificationInput(notification, type) {
  if (typeof notification === 'string') {
    const message = notification.trim()
    if (!message) return null
    return { type: type || 'info', title: '', message }
  }
  if (!notification || typeof notification !== 'object') return null
  const { title, message } = notificationCopy(notification)
  if (!title && !message) return null
  return {
    ...notification,
    title,
    message,
    type: notification.type || type || 'info',
  }
}

export function visibleToasts(notifications = [], max = MAX_VISIBLE_TOASTS) {
  return notifications
    .filter((item) => item && item.showToast !== false && !item.toastDismissed && hasNotificationCopy(item))
    .slice(0, max)
}

/** Le centre présente toujours la plus récente en premier. */
export function sortNotificationsByDate(notifications = []) {
  const time = (item) => {
    const date = item?.timestamp instanceof Date ? item.timestamp : new Date(item?.timestamp)
    return Number.isNaN(date?.getTime?.()) ? 0 : date.getTime()
  }
  return [...notifications].sort((left, right) => time(right) - time(left))
}

export function notificationSignature(notification = {}) {
  return [
    notification.type || 'info',
    notification.title || '',
    notification.message || '',
  ].join('\u0000')
}

/** Deux rendus successifs de la même alerte ne créent qu’une notification. */
export function findDuplicate(recent, signature, now, window = TOAST_DEDUPE_WINDOW) {
  const previous = recent?.get(signature)
  if (!previous) return null
  return now - previous.at < window ? previous : null
}

/**
 * Fusionne les toasts affichés : les nouveaux arrivent en tête, ceux qui
 * disparaissent gardent leur place le temps de l’animation de sortie.
 */
export function mergeToastSlots(previousSlots = [], visible = []) {
  const visibleById = new Map(visible.map((item) => [item.id, item]))
  const kept = previousSlots.map((slot) => {
    const next = visibleById.get(slot.notification.id)
    return next
      ? { notification: next, leaving: false }
      : { notification: slot.notification, leaving: true }
  })
  const known = new Set(previousSlots.map((slot) => slot.notification.id))
  const added = visible
    .filter((item) => !known.has(item.id))
    .map((item) => ({ notification: item, leaving: false }))
  return [...added, ...kept]
}

function twoDigits(value) {
  return String(value).padStart(2, '0')
}

/** Date secondaire : relative quand c’est récent, datée dès que l’alerte vieillit. */
export function formatNotificationDate(timestamp, now = new Date()) {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ''
  const reference = now instanceof Date ? now : new Date(now)
  const elapsed = reference.getTime() - date.getTime()
  const time = `${twoDigits(date.getHours())}:${twoDigits(date.getMinutes())}`

  if (elapsed < 60000) return 'À l’instant'
  if (elapsed < 3600000) return `il y a ${Math.floor(elapsed / 60000)} min`

  const startOfToday = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate())
  if (date >= startOfToday) return `aujourd’hui à ${time}`
  const startOfYesterday = new Date(startOfToday.getTime() - 86400000)
  if (date >= startOfYesterday) return `hier à ${time}`
  return `le ${twoDigits(date.getDate())}/${twoDigits(date.getMonth() + 1)} à ${time}`
}

/** Au-delà du seuil, l’alerte est de l’historique et non l’état courant du panier. */
export function isStaleNotification(timestamp, now = new Date(), maxAge = STALE_NOTIFICATION_AGE) {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  if (Number.isNaN(date.getTime())) return false
  const reference = now instanceof Date ? now : new Date(now)
  return reference.getTime() - date.getTime() >= maxAge
}

export function unreadLabel(count) {
  if (!count) return 'Tout est lu'
  return count === 1 ? '1 non lue' : `${count} non lues`
}

export const CLEAR_CARD_DURATION = 260
export const CLEAR_STAGGER = 48
export const CLEAR_TOTAL_CAP = 720

export function clearStaggerDelay(index, count, {
  duration = CLEAR_CARD_DURATION,
  stagger = CLEAR_STAGGER,
  cap = CLEAR_TOTAL_CAP,
} = {}) {
  if (!count || index <= 0) return 0
  const maxBudget = Math.max(0, cap - duration)
  const step = count <= 1 ? 0 : Math.min(stagger, maxBudget / Math.max(1, count - 1))
  return Math.round(index * step)
}

export function clearAllDuration(count, options) {
  if (!count || count < 1) return 0
  const duration = options?.duration ?? CLEAR_CARD_DURATION
  return clearStaggerDelay(count - 1, count, options) + duration
}

export function idsToClear(notifications = []) {
  return notifications.map((item) => item?.id).filter((id) => id != null)
}

/**
 * Message de budget : montants au format français et pourcentage arrondi vers le
 * bas pour ne jamais annoncer 100 % alors qu’il reste de l’argent.
 */
export function budgetNoticeText({ total, budget } = {}) {
  const budgetNum = Number(budget)
  const spent = Number(total)
  if (!Number.isFinite(budgetNum) || budgetNum <= 0 || !Number.isFinite(spent)) return null

  const remaining = Math.round((budgetNum - spent) * 100) / 100
  if (remaining < 0) {
    return {
      type: 'urgent',
      amount: formatEuroFr(Math.abs(remaining)),
      message: `Budget dépassé de ${formatEuroFr(Math.abs(remaining))}. Ajustement requis.`,
    }
  }

  const ratio = (spent / budgetNum) * 100
  if (ratio < 80) return null
  if (remaining === 0) {
    return {
      type: 'warning',
      percentage: 100,
      remaining: formatEuroFr(0),
      message: 'Budget entièrement utilisé, il ne reste rien à dépenser.',
    }
  }
  const percentage = Math.min(99, Math.floor(ratio))
  return {
    type: 'warning',
    percentage,
    remaining: formatEuroFr(remaining),
    message: `Budget utilisé à ${percentage} %. Il reste ${formatEuroFr(remaining)}.`,
  }
}
